import { createContext, useContext, useMemo, ReactNode } from 'react';

import {
    useLoadSchedulingProScript,
    useSchedulerObjectInitialized,
} from './hooks/useLoadSchedulingProScript';

interface SchedulerContextProps {
    schedulingProScriptLoadedSuccessfully: boolean;
}

const SchedulerContext = createContext<SchedulerContextProps>(null!);

interface SchedulingProProviderProps {
    children: ReactNode;
    dataApiKey: string;
    schedulerId: string;
    scriptUrl: string;
}

export function SchedulingProProvider({
    children,
    dataApiKey,
    schedulerId,
    scriptUrl,
}: SchedulingProProviderProps) {
    if (!dataApiKey || !schedulerId || !scriptUrl) {
        throw new Error('dataApiKey, schedulerId, and scriptUrl are required');
    }

    const schedulingProScriptLoadedSuccessfully = useLoadSchedulingProScript({
        dataApiKey,
        schedulerId,
        scriptUrl,
    });
    const schedulerObjectInitialized = useSchedulerObjectInitialized();

    const contextValue = useMemo(
        () => ({
            schedulingProScriptLoadedSuccessfully:
                schedulingProScriptLoadedSuccessfully && schedulerObjectInitialized,
        }),
        [schedulingProScriptLoadedSuccessfully, schedulerObjectInitialized]
    );

    return <SchedulerContext.Provider value={contextValue}>{children}</SchedulerContext.Provider>;
}

export function useSchedulingPro() {
    const context = useContext(SchedulerContext);
    if (!context) {
        throw new Error('Scheduling Pro components must be used within SchedulingProProvider');
    }
    return context;
}
